import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { apiBaseUrl } from '../../authConfig';
import type { Me } from '../../api/types';
import type { ListState } from '../common/AsyncList';

export type ConnectionStatus = 'Connected' | 'NotConfigured' | 'Error';

/** A service the Deployment Manager API depends on, as the API reports it. */
export interface Connection {
  id: string;
  name: string;
  target: string;
  status: ConnectionStatus;
  detail?: string;
}

/**
 * Live connection checks: the API itself (engineer token accepted), FirstIQ Guardian (through
 * the API's managed identity) and package storage (manifests readable).
 */
export function useConnections(): ListState<Connection> {
  const q = useQuery({
    queryKey: ['connections'],
    queryFn: async (): Promise<Connection[]> => {
      const probe = async (fn: () => Promise<unknown>) => { try { await fn(); return null; } catch (e) { return (e as Error).message; } };
      let me: Me | null = null;
      const apiErr = await probe(async () => { me = await api<Me>('me'); });
      const guardianErr = await probe(() => api('worklist'));
      const storageErr = await probe(() => api('products'));
      const row = (id: string, name: string, target: string, err: string | null, ok?: string): Connection =>
        ({ id, name, target, status: err ? 'Error' : 'Connected', detail: err ?? ok });
      return [
        row('api', 'Deployment Manager API', apiBaseUrl, apiErr, me ? `Signed in as ${(me as Me).engineer.username}` : undefined),
        row('guardian', 'FirstIQ Guardian', 'Licences and installation records (managed identity)', guardianErr),
        row('storage', 'Package storage', 'Product manifests and packages', storageErr),
        row('customer', 'Customer Connect app', me ? (me as Me).customerAppClientId : '—', apiErr, 'Multi-tenant app the customer Global Admin signs in with'),
      ];
    },
  });
  return { loading: q.isLoading, error: q.error ? (q.error as Error).message : null, items: q.data ?? [] };
}
